import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { H1, Sub, IconBtn } from "./_shared";

export default function ClubsSection() {
  const [clubs, setClubs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/clubs").then((r) => setClubs(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id, name) => { if (!window.confirm(`Delete ${name}?`)) return; try { await api.delete(`/admin/clubs/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } };
  const verify = async (id) => { try { await api.post(`/admin/clubs/${id}/verify`); toast.success("Verified"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } };
  return (
    <div data-testid="cp-clubs">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><H1>Clubs</H1><Sub>Verify, edit or remove clubs.</Sub></div>
        <button onClick={() => setCreating(true)} data-testid="cp-club-new" className="pp-btn-primary px-4 py-2 rounded-lg flex items-center gap-2"><Plus size={14} /> New club</button>
      </div>
      <div className="mt-6 space-y-2">
        {clubs.map((c) => (
          <div key={c.id} className="pp-card p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`cp-club-${c.id}`}>
            <div>
              <div className="font-bold flex items-center gap-2">{c.name}{c.is_verified ? <ShieldCheck size={14} className="text-[#00E676]" /> : null}</div>
              <div className="text-xs text-zinc-500">{c.city}, {c.country} · {c.email || "—"}</div>
            </div>
            <div className="flex gap-1">
              {!c.is_verified && <button onClick={() => verify(c.id)} data-testid={`cp-club-verify-${c.id}`} className="pp-btn-secondary px-3 py-1 rounded-lg text-xs">Verify</button>}
              <IconBtn onClick={() => setEditing(c)} data-testid={`cp-club-edit-${c.id}`}><Pencil size={14} /></IconBtn>
              <IconBtn onClick={() => del(c.id, c.name)} data-testid={`cp-club-del-${c.id}`} danger><Trash2 size={14} /></IconBtn>
            </div>
          </div>
        ))}
      </div>
      {(creating || editing) && (
        <ClubDialog club={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ClubDialog({ club, onClose, onSaved }) {
  const [f, setF] = useState({
    name: club?.name || "", description: club?.description || "",
    country: club?.country || "", city: club?.city || "",
    address: club?.address || "", phone: club?.phone || "",
    email: club?.email || "", website: club?.website || "",
    is_verified: club?.is_verified || false,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (club) await api.patch(`/admin/clubs/${club.id}`, f);
      else await api.post("/admin/clubs", f);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-lg" data-testid="cp-club-dialog">
        <DialogHeader><DialogTitle>{club ? `Edit ${club.name}` : "New club"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input className="pp-input" required placeholder="Name" value={f.name} onChange={set("name")} data-testid="cp-club-name" />
          <textarea className="pp-input" rows={2} placeholder="Description" value={f.description} onChange={set("description")} />
          <div className="grid grid-cols-2 gap-3">
            <input className="pp-input" placeholder="City" value={f.city} onChange={set("city")} />
            <input className="pp-input" placeholder="Country" value={f.country} onChange={set("country")} />
          </div>
          <input className="pp-input" placeholder="Address" value={f.address} onChange={set("address")} />
          <div className="grid grid-cols-2 gap-3">
            <input className="pp-input" placeholder="Phone" value={f.phone} onChange={set("phone")} />
            <input className="pp-input" placeholder="Email" value={f.email} onChange={set("email")} />
          </div>
          <input className="pp-input" placeholder="Website" value={f.website} onChange={set("website")} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_verified} onChange={(e) => setF((s) => ({ ...s, is_verified: e.target.checked }))} /> Verified</label>
          <DialogFooter>
            <button type="button" onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
            <button data-testid="cp-club-submit" className="pp-btn-primary px-4 py-2 rounded-lg">Save</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
